import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Bell, BellOff, Smartphone, Info, MessageSquare, Mail, Phone, Hash } from 'lucide-react';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useBadgeManager } from '@/hooks/useBadgeManager';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { TestNotifications } from './TestNotifications';
import { PushNotificationDebug } from './PushNotificationDebug';

export const NotificationSettings = () => {
  const { isSupported, isSubscribed, permission, loading, subscribe, unsubscribe, updatePreferences } = usePushNotifications();
  const { isSupported: badgeSupported, badgeEnabled, totalCount, toggleBadge } = useBadgeManager();
  const [preferences, setPreferences] = useState({
    sms: true,
    contact_form: true,
    phone_call: true
  });

  // Detect if running as PWA on iOS
  const isIOSPWA = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isStandalone = ('standalone' in navigator) && (navigator as any).standalone;
    return isIOS && isStandalone;
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const handleSubscribe = async () => {
    await subscribe(preferences);
  };

  const handlePreferenceChange = async (type: keyof typeof preferences, value: boolean) => {
    const newPreferences = { ...preferences, [type]: value };
    setPreferences(newPreferences);
    
    if (isSubscribed) {
      await updatePreferences(newPreferences);
    }
  };

  if (!isSupported) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Push Notifications</CardTitle>
            <CardDescription>
              Push notifications are not supported in this browser
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isIOS && (
              <Alert>
                <Smartphone className="h-4 w-4" />
                <AlertDescription>
                  <strong>iPhone/iPad Users:</strong> Push notifications require iOS 16.4+ and the app must be added to your home screen.
                  <ol className="mt-2 ml-4 list-decimal space-y-1 text-sm">
                    <li>Tap the Share button in Safari</li>
                    <li>Select "Add to Home Screen"</li>
                    <li>Open the app from your home screen</li>
                    <li>Enable notifications when prompted</li>
                  </ol>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isSubscribed ? (
              <Bell className="h-5 w-5 text-green-600" />
            ) : (
              <BellOff className="h-5 w-5 text-muted-foreground" />
            )}
            Push Notifications
          </CardTitle>
          <CardDescription>
            Get instant notifications for new leads and messages
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {permission === 'denied' && (
            <Alert>
              <AlertDescription>
                Push notifications are blocked. Please enable them in your browser settings.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="notifications-enabled" className="text-base font-medium">
                Enable Notifications
              </Label>
              <p className="text-sm text-muted-foreground">
                {isSubscribed 
                  ? 'You will receive push notifications for new leads'
                  : 'Subscribe to receive push notifications'
                }
              </p>
            </div>
            {isSubscribed ? (
              <Button
                variant="outline"
                onClick={unsubscribe}
                disabled={loading}
              >
                Disable
              </Button>
            ) : (
              <Button
                onClick={handleSubscribe}
                disabled={loading || permission === 'denied'}
              >
                Enable
              </Button>
            )}
          </div>

          {isSubscribed && (
            <>
              <div className="border-t pt-6">
                <h4 className="font-medium mb-4">Notification Types</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <Label htmlFor="sms-notifications" className="font-normal">
                          SMS Messages
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          New text messages from customers
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="sms-notifications"
                      checked={preferences.sms}
                      onCheckedChange={(checked) => handlePreferenceChange('sms', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <Label htmlFor="contact-form-notifications" className="font-normal">
                          Contact Forms
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          New contact form submissions
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="contact-form-notifications"
                      checked={preferences.contact_form}
                      onCheckedChange={(checked) => handlePreferenceChange('contact_form', checked)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <Label htmlFor="phone-call-notifications" className="font-normal">
                          Phone Calls
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          New phone call leads
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="phone-call-notifications"
                      checked={preferences.phone_call}
                      onCheckedChange={(checked) => handlePreferenceChange('phone_call', checked)}
                    />
                  </div>
                </div>
              </div>

              {badgeSupported && (
                <div className="border-t pt-6">
                  <h4 className="font-medium mb-4">App Icon Badge</h4>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Hash className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <Label htmlFor="badge-notifications" className="font-normal">
                          Show Count on App Icon
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Display total unread count ({totalCount}) on home screen icon
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="badge-notifications"
                      checked={badgeEnabled}
                      onCheckedChange={toggleBadge}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          <Alert>
            <AlertDescription>
              Push notifications will alert you in real-time when new leads come in, even when the app is closed.
            </AlertDescription>
          </Alert>

          {isIOS && !isIOSPWA() && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>iPhone Tip:</strong> For best results, add this app to your home screen:
                <ol className="mt-2 ml-4 list-decimal space-y-1 text-sm">
                  <li>Tap the Share button (square with arrow) in Safari</li>
                  <li>Scroll and tap "Add to Home Screen"</li>
                  <li>Tap "Add" to confirm</li>
                  <li>Open the app from your home screen</li>
                </ol>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {isSubscribed && <TestNotifications />}
      
      <PushNotificationDebug />
    </div>
  );
};
